import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, Gift, Eye, TrendingUp, Share2, 
  Download, BarChart3, Users, Calendar, Clock, MapPin,
  Settings, Megaphone, FileText, ExternalLink, Copy, 
  Edit, MoreVertical, ChevronRight, CheckCircle, AlertCircle,
  Star, Percent, ShieldCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// Sidebar navigation items
const sidebarItems = [
  { id: 'overview', label: 'Dashboard', icon: BarChart3 },
  { id: 'page', label: 'Build Page', icon: FileText },
  { id: 'pricing', label: 'Pricing & Inventory', icon: DollarSign },
  { id: 'publish', label: 'Publish', icon: CheckCircle },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'redemptions', label: 'Redemptions', icon: Users },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-400',
  };

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorClasses[color])}>
            <Icon className="w-5 h-5" />
          </div>
          {trend && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 border-0 text-xs">
              <TrendingUp className="w-3 h-3 mr-1" />
              {trend}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

const ExperienceDashboard = () => {
  const navigate = useNavigate();
  const { experienceId } = useParams();
  const { isAuthenticated, isPartner, user, loading: authLoading } = useAuth();
  const [experience, setExperience] = useState(null);
  const [stats, setStats] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Update experience field helper
  const updateExperience = async (updates) => {
    setSaving(true);
    try {
      await api.put(`/vouchers/${experienceId}`, updates);
      setExperience(prev => ({...prev, ...updates}));
      toast.success('Updated!');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Allow both partners and admins to access experience dashboard
    if (!isPartner && user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    fetchExperienceData();
  }, [isAuthenticated, isPartner, authLoading, experienceId, user]);

  const fetchExperienceData = async () => {
    try {
      const [expRes, statsRes, redemptionsRes] = await Promise.all([
        api.get(`/vouchers/${experienceId}`),
        api.get(`/partner/experiences/${experienceId}/stats`).catch(() => ({ data: null })),
        api.get(`/partner/experiences/${experienceId}/redemptions`).catch(() => ({ data: [] }))
      ]);
      
      setExperience(expRes.data);
      setStats(statsRes.data || generateStats(expRes.data));
      setRedemptions(redemptionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch experience:', error);
      toast.error('Failed to load experience');
      navigate('/partner/experiences');
    } finally {
      setLoading(false);
    }
  };

  // Stats based on real experience data (no fake/random data)
  const generateStats = (expData) => {
    const sold = expData?.quantity_sold || expData?.sold || 0;
    const price = expData?.price || 0;
    const grossSales = sold * price;
    const platformFee = grossSales * 0.30;
    const netSales = grossSales - platformFee;
    
    return {
      gross_sales: grossSales,
      net_sales: netSales,
      platform_fee: platformFee,
      vouchers_sold: sold,
      vouchers_redeemed: expData?.redeemed || 0,
      page_views: 0,
      conversion_rate: 0,
      payout_status: netSales > 0 ? 'available' : 'pending',
      available_payout: netSales,
    };
  };

  const copyExperienceLink = () => {
    const baseUrl = window.location.origin;
    const expUrl = experience?.custom_url 
      ? `${baseUrl}/exp/${experience.custom_url}`
      : `${baseUrl}/experiences/${experience?.id}`;
    navigator.clipboard.writeText(expUrl);
    toast.success('Experience link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Experience Not Found</h2>
          <Button onClick={() => navigate('/partner/experiences')}>Back to Experiences</Button>
        </div>
      </div>
    );
  }

  const redemptionRate = stats ? ((stats.vouchers_redeemed / Math.max(stats.vouchers_sold, 1)) * 100) : 0;
  const daysUntilExpiry = experience.valid_until 
    ? Math.ceil((new Date(experience.valid_until) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex">
      {/* Left Sidebar */}
      <aside className="w-64 bg-white dark:bg-gray-900 border-r flex flex-col fixed h-screen">
        {/* Back Button */}
        <div className="p-4 border-b space-y-2">
          <Button 
            variant="ghost" 
            className="w-full justify-start" 
            onClick={() => navigate('/partner/experiences')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Experiences
          </Button>
          {/* Admin Dashboard link for admins */}
          {user?.role === 'admin' && (
            <Button 
              variant="ghost" 
              className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
              onClick={() => navigate('/admin')}
              data-testid="admin-dashboard-link"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Admin Dashboard
            </Button>
          )}
        </div>

        {/* Experience Info */}
        <div className="p-4 border-b">
          <h2 className="font-bold text-foreground line-clamp-2">{experience.title}</h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            {experience.venue_name}
          </div>
          {experience.valid_until && (
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              Valid until {format(new Date(experience.valid_until), 'MMM d, yyyy')}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Badge 
              className={cn(
                experience.is_published 
                  ? "bg-green-100 text-green-700" 
                  : "bg-yellow-100 text-yellow-700"
              )}
            >
              {experience.is_published ? 'Published' : 'Draft'}
            </Badge>
            {experience.gold_price && (
              <Badge className="bg-yellow-400 text-yellow-900">
                <Star className="w-3 h-3 mr-1" />
                Gold
              </Badge>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'page') {
                    navigate(`/partner/experiences/${experienceId}/builder`);
                  } else {
                    setActiveSection(item.id);
                  }
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                  isActive 
                    ? "bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400" 
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                )}
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
                {item.id === 'page' && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="p-4 border-t space-y-2">
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => window.open(`/experiences/${experience.id}`, '_blank')}
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Experience
          </Button>
          <Button 
            className="w-full justify-start bg-purple-600 hover:bg-purple-700"
            onClick={copyExperienceLink}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Experience
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{experience.title}</h1>
            <p className="text-muted-foreground">Experience Dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={copyExperienceLink}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button variant="outline" onClick={() => navigate(`/partner/syndication/${experienceId}`)}>
              <Share2 className="w-4 h-4 mr-2" />
              Syndicate
            </Button>
            <Button 
              onClick={() => navigate(`/partner/experiences/${experienceId}/builder`)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Experience
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Download className="w-4 h-4 mr-2" />
                  Export Redemptions
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate Experience
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600">
                  Deactivate Experience
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Net Revenue"
                value={`$${stats?.net_sales?.toFixed(2) || '0.00'}`}
                subtitle="After platform fees"
                icon={DollarSign}
                color="green"
              />
              <MetricCard
                title="Vouchers Sold"
                value={stats?.vouchers_sold || 0}
                subtitle={`${stats?.vouchers_redeemed || 0} redeemed`}
                icon={Gift}
                color="purple"
              />
              <MetricCard
                title="Page Views"
                value={stats?.page_views || 0}
                icon={Eye}
                color="blue"
              />
              <MetricCard
                title="Redemption Rate"
                value={`${redemptionRate.toFixed(0)}%`}
                icon={Percent}
                color="orange"
              />
            </div>

            {/* Expiry Warning */}
            {daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0 && (
              <Card className="border-0 shadow-sm bg-yellow-50 dark:bg-yellow-950">
                <CardContent className="p-4 flex items-center gap-4">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      Experience expires in {daysUntilExpiry} days
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Consider extending the validity period or promoting this experience.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto border-yellow-400">
                    Extend Validity
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Payout & Stats */}
              <div className="lg:col-span-2 space-y-6">
                {/* Payout Card */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Payout Summary</CardTitle>
                    <CardDescription>Your earnings from this experience</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-muted-foreground">Gross Sales</p>
                        <p className="text-xl font-bold">${stats?.gross_sales?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-muted-foreground">Platform Fee (30%)</p>
                        <p className="text-xl font-bold text-red-600">-${stats?.platform_fee?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                        <p className="text-sm text-muted-foreground">Available Payout</p>
                        <p className="text-xl font-bold text-green-600">${stats?.available_payout?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                    {stats?.available_payout > 0 && (
                      <div className="mt-4 flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium">Funds available for payout</span>
                        </div>
                        <Button size="sm">Request Payout</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Redemption Progress */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Redemption Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{stats?.vouchers_redeemed || 0}</span>
                        <span className="text-muted-foreground">of {stats?.vouchers_sold || 0} sold</span>
                      </div>
                      <Progress value={redemptionRate} className="h-3" />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{redemptionRate.toFixed(0)}% redeemed</span>
                        <span className="text-muted-foreground">{(stats?.vouchers_sold - stats?.vouchers_redeemed) || 0} pending</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Redemptions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Recent Redemptions</CardTitle>
                      <CardDescription>Latest voucher redemptions</CardDescription>
                    </div>
                    <Button variant="outline" size="sm">View All</Button>
                  </CardHeader>
                  <CardContent>
                    {redemptions.length === 0 ? (
                      <div className="text-center py-8">
                        <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No redemptions yet</p>
                        <p className="text-sm text-muted-foreground">Redemptions will appear here as vouchers are used</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Redeemed</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {redemptions.slice(0, 5).map((redemption) => (
                            <TableRow key={redemption.id}>
                              <TableCell className="font-mono text-sm">{redemption.code}</TableCell>
                              <TableCell>{redemption.customer_name}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {format(new Date(redemption.redeemed_at), 'MMM d, h:mm a')}
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-green-100 text-green-700">Redeemed</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Quick Actions */}
              <div className="space-y-6">
                {/* Experience Link */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Experience Link</CardTitle>
                    <CardDescription>Share with your audience</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3">
                      <p className="text-sm font-mono break-all">
                        {experience.custom_url 
                          ? `eventgoers.com/exp/${experience.custom_url}`
                          : `eventgoers.com/experiences/${experience.id.slice(-8)}`
                        }
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={copyExperienceLink}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      <Button variant="outline" className="flex-1">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Pricing Info */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Pricing</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div>
                          <p className="font-medium">Regular Price</p>
                          <p className="text-2xl font-bold">${experience.price}</p>
                        </div>
                      </div>
                      {experience.gold_price && (
                        <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              Gold Member Price
                              <Badge className="bg-yellow-400 text-yellow-900 text-xs">VIP</Badge>
                            </p>
                            <p className="text-2xl font-bold">${experience.gold_price}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-green-600 font-medium">
                              Save ${(experience.price - experience.gold_price).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate(`/partner/experiences/${experienceId}/builder`)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Experience Page
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Megaphone className="w-4 h-4 mr-2" />
                      Create Marketing Campaign
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Download Reports
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Settings className="w-4 h-4 mr-2" />
                      Experience Settings
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Section */}
        {activeSection === 'pricing' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Pricing & Inventory</CardTitle>
              <CardDescription>Manage pricing and availability</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 border rounded-lg">
                  <h3 className="font-bold mb-4">Current Pricing</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Regular Price</span>
                      <span className="text-2xl font-bold">${experience.price}</span>
                    </div>
                    {experience.gold_price && (
                      <div className="flex justify-between items-center">
                        <span>Gold Member Price</span>
                        <span className="text-2xl font-bold text-yellow-600">${experience.gold_price}</span>
                      </div>
                    )}
                  </div>
                  <Button className="w-full mt-4" variant="outline" onClick={() => {
                    const newPrice = prompt('Enter new price ($):', experience.price);
                    if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
                      updateExperience({ price: parseFloat(newPrice) });
                    }
                  }}>Edit Pricing</Button>
                </div>
                <div className="p-6 border rounded-lg">
                  <h3 className="font-bold mb-4">Validity</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Valid Until</span>
                      <span className="font-medium">
                        {experience.valid_until 
                          ? format(new Date(experience.valid_until), 'MMMM d, yyyy')
                          : 'No expiration'
                        }
                      </span>
                    </div>
                    {daysUntilExpiry !== null && (
                      <div className="flex justify-between items-center">
                        <span>Days Remaining</span>
                        <Badge className={daysUntilExpiry <= 7 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                          {daysUntilExpiry} days
                        </Badge>
                      </div>
                    )}
                  </div>
                  <Button className="w-full mt-4" variant="outline" onClick={() => {
                    const newDate = prompt('Enter new valid-until date (YYYY-MM-DD):', experience.valid_until || '');
                    if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
                      updateExperience({ valid_until: newDate });
                    } else if (newDate) {
                      toast.error('Use format YYYY-MM-DD');
                    }
                  }}>Extend Validity</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Redemptions Section */}
        {activeSection === 'redemptions' && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Redemptions</CardTitle>
                <CardDescription>{stats?.vouchers_redeemed || 0} vouchers redeemed</CardDescription>
              </div>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {redemptions.length === 0 ? (
                <div className="text-center py-8">
                  <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No redemptions yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Redeemed</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {redemptions.map((redemption) => (
                      <TableRow key={redemption.id}>
                        <TableCell className="font-mono">{redemption.code}</TableCell>
                        <TableCell className="font-medium">{redemption.customer_name}</TableCell>
                        <TableCell>{redemption.customer_email}</TableCell>
                        <TableCell>{format(new Date(redemption.redeemed_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-700">Redeemed</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Marketing Section */}
        {activeSection === 'marketing' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Marketing Tools</CardTitle>
              <CardDescription>Promote your experience and increase sales</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <Megaphone className="w-8 h-8 text-purple-600 mb-3" />
                    <h3 className="font-bold mb-1">AI Marketing Suite</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Generate social posts, emails, and ad copy with AI
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/partner/ai-marketing')}
                    >
                      Open AI Suite
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <Share2 className="w-8 h-8 text-indigo-600 mb-3" />
                    <h3 className="font-bold mb-1">Social Sharing</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Share your experience across social platforms
                    </p>
                    <Button variant="outline" className="w-full" onClick={copyExperienceLink}>
                      Get Share Link
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reports Section */}
        {activeSection === 'reports' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Reports & Analytics</CardTitle>
              <CardDescription>Detailed insights about your experience performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">Detailed reports coming soon</p>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Basic Report
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Publish Section */}
        {activeSection === 'publish' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Publish Settings</CardTitle>
              <CardDescription>Control when and how your experience is visible</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium">Experience Status</p>
                    <p className="text-sm text-muted-foreground">
                      {experience.is_published ? 'Your experience is live and visible to the public' : 'Your experience is in draft mode'}
                    </p>
                  </div>
                  <Badge className={experience.is_published ? 'bg-green-500' : 'bg-yellow-500'}>
                    {experience.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">Custom Experience URL</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Create a memorable URL for your experience
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-gray-50 dark:bg-gray-800 rounded-lg px-3">
                      <span className="text-muted-foreground text-sm">eventgoers.com/exp/</span>
                      <input 
                        type="text"
                        placeholder="your-experience-name"
                        className="flex-1 bg-transparent border-0 outline-none text-sm py-2"
                        defaultValue={experience.custom_url || ''}
                      />
                    </div>
                    <Button onClick={() => {
                      const urlInput = document.querySelector('input[placeholder="your-experience-name"]');
                      if (urlInput?.value) { updateExperience({ custom_url: urlInput.value }); }
                    }}>Save URL</Button>
                  </div>
                </div>

                {!experience.is_published && (
                  <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => updateExperience({ is_published: true, active: true })}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Publish Experience
                  </Button>
                )}
                {experience.is_published && (
                  <Button className="w-full" variant="outline" onClick={() => updateExperience({ is_published: false })}>
                    Unpublish (Set to Draft)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ExperienceDashboard;
